import { useState } from "react";
import { z } from "zod";
import { MessageCircle } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";

const schema = z.object({
  name: z.string().trim().min(1, "Please add your name").max(100),
  business: z.string().trim().max(100).optional(),
  contact: z.string().trim().min(5, "Add a phone number or email").max(255),
  need: z.string().trim().min(5, "Tell us a little about what you need").max(800),
});

const services = ["Lead generation", "Video production", "Drone videography", "Other services", "Not sure yet"];

const field =
  "w-full rounded-xl border-2 border-white bg-white px-4 py-3 text-primary placeholder:text-primary/60 focus:outline-none focus:ring-4 focus:ring-white/40";

const EnquiryForm = ({ preset }: { preset?: string }) => {
  const options = preset && !services.includes(preset) ? [preset, ...services] : services;
  const [form, setForm] = useState({ name: "", business: "", contact: "", need: "", service: preset || services[0] });
  const [error, setError] = useState("");
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const r = schema.safeParse(form);
    if (!r.success) return setError(r.error.issues[0].message);
    setError("");
    const d = r.data;
    const text = [
      `Hi Stag, I'd like to book a call.`,
      `Name: ${d.name}`,
      d.business ? `Business: ${d.business}` : "",
      `Interested in: ${form.service}`,
      `What I need: ${d.need}`,
      `Best contact: ${d.contact}`,
    ].filter(Boolean).join("\n");
    window.open(`https://wa.me/447403682194?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <form onSubmit={submit} className="mx-auto grid max-w-2xl gap-4 text-left" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1">Your name<input className={field} value={form.name} onChange={set("name")} maxLength={100} autoComplete="name" /></label>
        <label className="grid gap-1">Business name (optional)<input className={field} value={form.business} onChange={set("business")} maxLength={100} autoComplete="organization" /></label>
        <label className="grid gap-1">Phone or email<input className={field} value={form.contact} onChange={set("contact")} maxLength={255} /></label>
        <label className="grid gap-1">What can we help with?
          <select className={field} value={form.service} onChange={set("service")}>{options.map((s) => <option key={s}>{s}</option>)}</select>
        </label>
      </div>
      <label className="grid gap-1">Tell us about your business and goals<textarea className={`${field} min-h-28`} value={form.need} onChange={set("need")} maxLength={800} /></label>
      {error && <p role="alert" className="font-semibold text-white">{error}</p>}
      <button type="submit" className="magnetic-btn cta-glow inline-flex items-center justify-center gap-2 rounded-full border-2 border-white bg-white px-10 py-4 font-semibold text-primary hover:bg-white/90">
        <MessageCircle size={20} /> Send and book a call on WhatsApp
      </button>
      <p className="text-center text-white">Prefer to talk now? Call <a href={PHONE_TEL} className="font-semibold underline">{PHONE_DISPLAY}</a></p>
    </form>
  );
};

export default EnquiryForm;
